// Last-resort tier: no WebGL2, no 2D context, or a GPU context that was lost and
// never came back. The blob becomes a CSS gradient disc that still changes
// colour with state, which is precisely what the design language already asks
// for under prefers-reduced-motion.
//
// It paints the caller's own <canvas> element rather than inserting DOM of its
// own. A canvas with nothing drawn into it is transparent, so a CSS background
// on that element shows through, the page keeps the layout it already computed,
// and no selector the caller wrote stops matching.

import type { BlobFrame } from "./palette";
import { paletteCss } from "./palette";

const STYLE_ID = "blob-css-fallback";
const ATTR = "data-blob-css";

// Custom properties have to be registered before they can be transitioned;
// without @property the colour would snap instead of crossfading. Browsers that
// do not know the at-rule ignore the block and get the snap, which is an
// acceptable degradation for a tier that is already the last one.
const CSS = `
@property --blob-body { syntax: "<color>"; inherits: false; initial-value: #dfd8cc; }
@property --blob-lit  { syntax: "<color>"; inherits: false; initial-value: #ece7de; }

[${ATTR}] {
  border-radius: 50%;
  background: radial-gradient(circle at 34% 30%,
    var(--blob-lit) 0%, var(--blob-body) 72%);
  transition: --blob-body 200ms linear, --blob-lit 200ms linear;
  animation: blob-css-breath 5200ms cubic-bezier(.65, 0, .35, 1) infinite;
  will-change: transform;
}

@keyframes blob-css-breath {
  0%, 100% { transform: scale(1); }
  42%      { transform: scale(1.018); }
}

@media (prefers-reduced-motion: reduce) {
  [${ATTR}] { animation: none; }
}
`;

// One stylesheet is shared by every instance and removed when the last one goes.
let sheet: HTMLStyleElement | null = null;
let mounted = 0;

function attachSheet(): void {
  mounted += 1;
  if (sheet) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
  sheet = el;
}

function detachSheet(): void {
  mounted -= 1;
  if (mounted > 0 || !sheet) return;
  sheet.remove();
  sheet = null;
}

export class CssBlob {
  private body = "";

  constructor(private readonly canvas: HTMLCanvasElement) {
    attachSheet();
    canvas.setAttribute(ATTR, "");
  }

  /** The drawing buffer is unused here; CSS sizes itself from the element. */
  resize(): void {
    // Intentionally empty.
  }

  paint(f: BlobFrame): void {
    // The design's two-stop gradient: the state colour, and a 6% lighter version
    // of itself.
    const body = paletteCss(f.coeffs);
    if (body === this.body) return;
    this.body = body;
    this.canvas.style.setProperty("--blob-body", body);
    this.canvas.style.setProperty("--blob-lit", paletteCss(f.coeffs, 0.06));
  }

  destroy(): void {
    this.canvas.removeAttribute(ATTR);
    this.canvas.style.removeProperty("--blob-body");
    this.canvas.style.removeProperty("--blob-lit");
    detachSheet();
  }
}
